import { useFormik } from "formik"
import { useGetDealDoneByUsersQuery, useGetTimeOfDayQuery } from "../../../../pages/Common/commonService"
import * as Yup from "yup"
import { useMemo } from "react"
import { ISoloSurveyForm, setSoloFormModule } from "../../RaiseTicketFormSlice"
import { useDispatch, useSelector } from "react-redux"
import { TStore } from "../../../../store/Store"

const validationSchema = Yup.object({
    visit_by_date: Yup.string().required("Required"),
    project_name: Yup.string().required("Required"),
    visit_by_time: Yup.object({
        id: Yup.number().required(),
        title: Yup.string().required(),
    }).required(),
    load_available: Yup.number().required("Required").typeError("Must be a number"),
    connection_type: Yup.object({
        id: Yup.number().required(),
        title: Yup.string().required(),
    }).required(),
    business_poc: Yup.object()
        .test("is-not-empty", "Business POC is required", value => value && Object.keys(value).length > 0)
        .required("Business POC is required"),
    foundation_type: Yup.object({
        id: Yup.number().required(),
        title: Yup.string().required(),
    }).required(),
    demand_load: Yup.number().required("Required").typeError("Must be a number"),
    sanctioned_load: Yup.number().required("Required").typeError("Must be a number"),
    spot_image: Yup.mixed()
        .test("has-file", "At least one spot image is required", value => {
            if (!value) return false
            const files = Array.isArray(value) ? value : typeof value === "object" ? Object.values(value) : []
            return files.length > 0
        })
        .required("Spot image is required"),

    electricity_bill: Yup.mixed()
        .test("has-file", "Electricity bill is required", value => {
            if (!value) return false
            const files = Array.isArray(value) ? value : typeof value === "object" ? Object.values(value) : []
            return files.length > 0
        })
        .required("Electricity bill is required"),
    chargers: Yup.array()
        .of(
            Yup.object().shape({
                type: Yup.object().nullable().required("Required"),
                quantity: Yup.number()
                    .required("Required")
                    .typeError("Must be a number")
                    .min(1, "Quantity must be at least 1"),
                rating: Yup.number()
                    .required("Required")
                    .typeError("Must be a number")
                    .min(1, "Rating must be at least 1"),
            })
        )
        .min(1, "At least one charger is required")
        .test(
            "all-filled",
            "All charger fields must be filled",
            chargers =>
                chargers && chargers.every(ch => ch.type && ch.quantity !== undefined && ch.rating !== undefined)
        ),
})

export const useSoloSurveyForm = (handleNextStep: () => void) => {
    const { data: timeOfDayList } = useGetTimeOfDayQuery()
    const { data: dealDoneByUsersList } = useGetDealDoneByUsersQuery()
    const { solo_survey_form } = useSelector((state: TStore) => state.RaiseTicketForm)
    const dispatch = useDispatch()
    const formik = useFormik<ISoloSurveyForm>({
        initialValues: solo_survey_form,
        validationSchema,
        onSubmit: values => {
            handleNextStep()
            dispatch(setSoloFormModule(values))
        },
    })

    const today = useMemo(() => new Date(), [])
    const minDate = useMemo(() => {
        const d = new Date()
        d.setDate(today.getDate() + 2)
        return d.toISOString().split("T")[0]
    }, [today])

    return {
        formik,
        dealDoneByUsersList,
        timeOfDayOptions:
            timeOfDayList?.data?.map((data: any) => ({
                id: data?.id,
                title: data?.name,
            })) || [],
        minDateFormatted: minDate,
    }
}
